import type { CastAnchor, StageSpec } from "../config/campaign";

/** Proporção entre largura do rosto e distância interocular. Serve para manter o rosto dentro da tela. */
const FACE_WIDTH_RATIO = 2.3;

export type StageFigure = {
  id: string;
  /** Dimensões naturais do recorte. */
  width: number;
  height: number;
  anchor: CastAnchor;
  /** Maior valor desenha por cima. */
  depth: number;
};

export type StagePlacement = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
};

/** Largura do círculo na altura indicada. Zero quando a linha passa fora do círculo. */
export function chordWidth(circle: readonly [number, number, number], y: number) {
  const [, centerY, radius] = circle;
  const half = radius ** 2 - (y - centerY) ** 2;
  return half <= 0 ? 0 : 2 * Math.sqrt(half);
}

/** Quantos parceiros o formato comporta, além do apoiador e do candidato principal. */
export function maxPartnersFor(spec: StageSpec): number {
  const counts = Object.keys(spec.byCount).map(Number).filter(Number.isFinite);
  if (counts.length === 0) return 0;
  return Math.max(0, Math.max(...counts) - 2);
}

/**
 * Distribui as figuras normalizando a escala pela distância interocular e alinhando
 * a linha dos olhos. É o que faz recortes de origens diferentes parecerem a mesma foto.
 * O retorno já vem na ordem de desenho, de trás para frente.
 */
export function layoutStage(
  figures: StageFigure[],
  spec: StageSpec,
  canvasWidth: number,
  margin = 24,
  /** Largura realmente aproveitável, centrada no canvas. Numa moldura circular é a corda
   *  do círculo na linha dos olhos, bem menor que o canvas — sem isso os rostos das pontas
   *  ficam dentro da imagem mas fora do recorte visível. */
  usableWidth = canvasWidth,
): StagePlacement[] {
  const count = figures.length;
  const placement = spec.byCount[String(count)];
  if (!placement) throw new Error(`Sem arranjo definido para ${count} figuras.`);

  const faceHalfWidth = (placement.interocular * FACE_WIDTH_RATIO) / 2;
  const room = Math.min(canvasWidth, usableWidth) - 2 * faceHalfWidth - 2 * margin;
  const spacing = count > 1 ? Math.min(placement.spacing, Math.max(0, room / (count - 1))) : 0;
  const startX = canvasWidth / 2 - (spacing * (count - 1)) / 2;

  return [...figures]
    .sort((a, b) => a.depth - b.depth)
    .map((figure) => {
      const index = figures.indexOf(figure);
      const naturalInterocular = figure.anchor.interocular * figure.width;
      const scale = placement.interocular / naturalInterocular;
      const width = figure.width * scale;
      const height = figure.height * scale;
      return {
        id: figure.id,
        x: startX + spacing * index - figure.anchor.eyeX * width,
        y: placement.eyeLineY - figure.anchor.eyeY * height,
        width,
        height,
        scale,
      };
    });
}
